import {
  type PropertiesConfig,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSection,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME } from '@blaze-shared/schema-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import {
  COUNTDOWN_LABELS_POSITION_OPTIONS,
  COUNTDOWN_SHOW_OPTIONS,
  COUNTDOWN_TEMPLATE_OPTIONS,
  type CountdownLabelsPosition,
  type CountdownTemplate,
  type CountdownTimerPropsModel,
} from './countdown-timer.props-model';

export function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('countdownTimer', [getContentSection(), getStyleSection()]);
}

function getContentSection(): PropertiesConfigSection {
  return epc.section('content', 'Content', [
    epc.subsections.dataSubsection(
      'data.?key:value.enabled',
      'Data',
      [
        epc.fields.sliderField('content.options.duration', 'Duration', [
          { min: 1, max: 30, unit: 'days' },
          { min: 1, max: 23, unit: 'hrs' },
          { min: 1, max: 60, unit: 'min' },
          { min: 1, max: 60, unit: 's' },
        ]),
      ],
      [
        epc.fields.selectDataSourceField({
          key: 'value',
          label: 'Value',
          sources: 'list',
          bindings: [],
        }),

        ...epc.utils.conditionalFields(
          [
            epc.fields.selectFieldFromSchema(
              'content.data.targetField',
              'Target',
              'data.?key:value.dataSource',
              {
                valueField: COLUMN_ID_OR_NAME,
                optionsFilter: epc.utils.columnDataTypeFilter([
                  'Date',
                  'DateTime',
                  'Duration',
                  'Number',
                ]),
              },
            ),
          ],
          'data.?key:value.dataSource',
          (value) => !value,
        ),
      ],
    ),
    epc.subsections.subsection('content.options', 'Template', [
      epc.utils.observedField(
        epc.fields.selectFieldFromList('content.options.template', '', COUNTDOWN_TEMPLATE_OPTIONS, {
          hideLabel: true,
          showSelection: true,
          allowNone: false,
        }),
        (model: CountdownTimerPropsModel, value: CountdownTemplate) => {
          const result: Record<string, string> = {};
          const labelPos = getValueByPath(
            model,
            'content.options.labelsPosition',
          ) as CountdownLabelsPosition;
          if (value === 'inline' && labelPos !== 'right') {
            result['content.options.labelsPosition'] = 'right';
          } else if (value !== 'inline' && labelPos !== 'bottom') {
            result['content.options.labelsPosition'] = 'bottom';
          }

          if (value === 'block') {
            result['style.time.value.fontSize'] = '40px';
            result['style.time.label.fontSize'] = '13px';
          } else if (value === 'inline') {
            result['style.time.value.fontSize'] = '24px';
            result['style.time.label.fontSize'] = '16px';
          } else if (value === 'clock') {
            result['content.options.duration'] = '1min';
            result['root.height.value'] = 200 as any;
          }
          return result;
        },
      ),
    ]),
    epc.subsections.subsection(
      'content.options',
      'Options',
      [
        epc.fields.checksField('content.options.show', 'Show', {
          options: COUNTDOWN_SHOW_OPTIONS,
          renderMode: 'separated',
          togglesContainerCssClass: 'gap-8',
          toggleCssClass: 'flex-grow',
        }),
        epc.fields.radiosField('content.options.labelsPosition', 'Labels', [], {
          renderType: 'icon',
          options: (props) => {
            const template = getValueByPath(props, 'content.options.template') as CountdownTemplate;
            if (template === 'inline') {
              return COUNTDOWN_LABELS_POSITION_OPTIONS;
            }

            return COUNTDOWN_LABELS_POSITION_OPTIONS.filter(
              (v) => v.value === 'top' || v.value === 'bottom',
            );
          },
          stretched: false,
          controlAlign: 'center',
        }),
        epc.utils.conditionalField(
          epc.fields.toggleField('content.options.showDividers', 'Show dividers'),
          'content.options.template',
          (value) => (value as CountdownTemplate) !== 'inline',
        ),
      ],
      {
        hideWhen: (_, __, props: CountdownTimerPropsModel) =>
          props.content.options.template === 'clock',
      },
    ),

    epc.subsections.subsection('content.data', 'Actions', [
      epc.fields.actionConfig('content.data.action', {
        label: 'When time is done',
        isCompact: true,
        labelWidth: '55%',
        eventType: 'action',
      }),
    ]),

    epc.subsections.visibilitySubsection(),
    epc.subsections.labelContentSubsection('content'),
  ]);
}

function getStyleSection(): PropertiesConfigSection {
  return epc.section(
    'style',
    'Style',
    [
      epc.subsections.styleSheetSubsection(),
      epc.subsections.subsection('style.general', 'Margin', [
        epc.fields.spacingAndAlignField('style.general.offset', {
          hasMargin: true,
        }),
      ]),

      epc.subsections.subsection('', 'Day Style', getDayStyleProps(), {
        hideWhen: (_, __, props: CountdownTimerPropsModel) => {
          return props?.content?.options?.template !== 'block';
        },
      }),

      epc.subsections.subsection('', 'Time Style', getTimeStyleProps(), {
        hideWhen: (_, __, props: CountdownTimerPropsModel) => {
          return props?.content?.options?.template === 'clock';
        },
      }),

      epc.subsections.subsection('', 'Label', getLabelStyleProps(), {
        hideWhen: (_, __, props: CountdownTimerPropsModel) => {
          return props?.content?.options?.template !== 'clock';
        },
      }),

      epc.subsections.subsection('', 'Clock', getClockStyleProps(), {
        hideWhen: (_, __, props: CountdownTimerPropsModel) => {
          return props?.content?.options?.template !== 'clock';
        },
      }),

      epc.subsections.labelStyleSubsection(
        {
          enabled: 'content.title.enabled',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          position: 'content.title.position',
          width: 'content.title.width',
          color: 'style.general.title',
          fontFamily: 'style.general.fontFamily',
          fontSize: 'style.general.fontSize',
          fontStyle: 'style.general.fontStyle',
          fontWeight: 'style.general.fontWeight',
          iconSize: 'style.general.iconSize',
          lineHeight: 'style.general.lineHeight',
        },
        {
          overrides: {
            horizontalAlign: epc.fields.horizontalAlignField('content.title.align', {
              defaultValue: 'left',
            }),
          },
        },
      ),
    ],
    true,
  );
}

function getDayStyleProps(): PropertiesConfigItemDefinition[] {
  return [
    ...epc.fields.fontFamilyAndWeightFields('style.day.fontFamily', 'style.day.fontWeight'),
    epc.fields.fontStyleField('style.day.fontStyle', 'Style', ['bold']),
    epc.fields.sliderField('style.day.fontSize', 'Size', [{ min: 8, max: 72, unit: 'px' }]),
    epc.fields.colorField('style.day.value', 'Color', { allowGradient: false, allowNone: false }),
    epc.utils.separatedField(epc.fields.colorField('style.day.fill', 'Fill', {})),
    epc.fields.colorField('style.day.border', 'Outline', { allowGradient: false }),
    epc.fields.sliderField('style.day.borderWidth', 'Width', [{ min: 0, max: 10, unit: 'px' }]),
    epc.fields.radiusField('style.day.radius'),
  ];
}
function getTimeStyleProps(): PropertiesConfigItemDefinition[] {
  return [
    epc.fields.radiosField(
      'style.time.textPart',
      '',
      [
        { value: 'value', label: 'Number' },
        { value: 'label', label: 'Label' },
      ],
      { hideLabel: true },
    ),

    ...epc.utils.conditionalFields(
      [
        ...epc.fields.fontFamilyAndWeightFields(
          'style.time.value.fontFamily',
          'style.time.value.fontWeight',
        ),
        epc.fields.fontStyleField('style.time.value.fontStyle', 'Style', ['bold']),
        epc.fields.sliderField('style.time.value.fontSize', 'Size', [
          { min: 8, max: 72, unit: 'px' },
        ]),
        epc.fields.colorField('style.time.value.value', 'Color', {
          allowGradient: false,
          allowNone: false,
        }),
      ],
      'style.time.textPart',
      (part) => part === 'label',
    ),

    ...epc.utils.conditionalFields(
      [
        ...epc.fields.fontFamilyAndWeightFields(
          'style.time.label.fontFamily',
          'style.time.label.fontWeight',
        ),
        epc.fields.fontStyleField('style.time.label.fontStyle', 'Style', ['bold']),
        epc.fields.sliderField('style.time.label.fontSize', 'Size', [
          { min: 8, max: 72, unit: 'px' },
        ]),
        epc.fields.colorField('style.time.label.value', 'Color', {
          allowGradient: false,
          allowNone: false,
        }),
      ],
      'style.time.textPart',
      (part) => part !== 'label',
    ),

    epc.utils.separatedField(epc.fields.colorField('style.time.fill', 'Fill', {})),
    epc.fields.colorField('style.time.border', 'Outline', { allowGradient: false }),
    epc.fields.sliderField('style.time.borderWidth', 'Width', [{ min: 0, max: 10, unit: 'px' }]),
    epc.fields.radiusField('style.time.radius'),

    ...epc.utils.conditionalFields(
      [
        epc.utils.separatedField(
          epc.fields.colorField('style.time.divider', 'Divider', { allowGradient: false }),
          { hideSeparatorWhenHidden: true },
        ),
        epc.fields.sliderField('style.time.dividerWidth', 'Width', [
          { min: 1, max: 10, unit: 'px' },
        ]),
      ],
      'content.options.template',
      (part: CountdownTemplate) => part !== 'inline',
    ),
  ];
}

function getLabelStyleProps(): PropertiesConfigItemDefinition[] {
  return [
    ...epc.fields.fontFamilyAndWeightFields('style.label.fontFamily', 'style.label.fontWeight'),
    epc.fields.fontStyleField('style.label.fontStyle', 'Style', ['bold']),
    epc.fields.sliderField('style.label.fontSize', 'Size', [{ min: 8, max: 72, unit: 'px' }]),
    epc.fields.colorField('style.label.value', 'Color', { allowGradient: false, allowNone: false }),
    epc.utils.separatedField(
      epc.fields.colorField('style.label.divider', 'Color', {
        allowGradient: false,
        allowNone: false,
      }),
    ),
  ];
}
function getClockStyleProps(): PropertiesConfigItemDefinition[] {
  return [
    epc.fields.colorField('style.clock.gutter', 'Gutter', {
      allowGradient: false,
      allowNone: false,
    }),
    epc.fields.colorField('style.clock.active', 'Active', {
      allowGradient: false,
      allowNone: false,
    }),
    epc.fields.sliderField('style.clock.width', 'Width', [{ min: 1, max: 30, unit: 'px' }]),
  ];
}
